import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import { markNotificationRead } from "../api/notifications";
import {
  cancelOrder,
  completeOrder,
  confirmOrder,
  getOrder,
  getOrders,
  markOrderPreparing,
  markOrderReady,
  rejectOrder,
} from "../api/orders";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import VariantSnapshotLabel from "../components/VariantSnapshotLabel";
import { useAuth } from "../context/AuthContext";
import useDebouncedValue from "../hooks/useDebouncedValue";
import PageHeader from "../components/PageHeader";
import MainLayout from "../layouts/MainLayout";
import { formatDate, formatMoney, titleCase } from "../utils/format";

const PAGE_SIZE = 24;
const STATUSES = [
  "",
  "awaiting_customer_confirmation",
  "confirmed",
  "preparing",
  "ready",
  "completed",
  "cancelled",
  "expired",
  "rejected",
];

function statusColor(value) {
  if (["completed", "ready"].includes(value)) return "success";
  if (["confirmed", "preparing"].includes(value)) return "info";
  if (value === "awaiting_customer_confirmation") return "warning";
  if (["cancelled", "expired", "rejected"].includes(value)) return "error";
  return "default";
}

function nextActions(order) {
  if (!order) return [];
  if (order.status === "awaiting_customer_confirmation") return ["confirm", "reject", "cancel"];
  if (order.status === "confirmed") return ["preparing", "complete", "cancel"];
  if (order.status === "preparing") return ["ready", "complete", "cancel"];
  if (order.status === "ready") return ["complete", "cancel"];
  return [];
}

export default function Orders() {
  const { canEdit, selectedStore } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [saving, setSaving] = useState(false);
  const markedNotifications = useRef(new Set());
  const debouncedQuery = useDebouncedValue(query.trim(), 300);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getOrders({
        query: debouncedQuery || undefined,
        status: statusFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(response.data?.items || []);
      setTotal(Number(response.data?.total || 0));
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load orders."));
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, page, statusFilter]);

  useEffect(() => {
    load();
  }, [load, selectedStore?.public_id]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, statusFilter, selectedStore?.public_id]);

  const openOrder = useCallback(async (publicId) => {
    setDetailLoading(true);
    setError("");
    try {
      const response = await getOrder(publicId);
      setSelected(response.data);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load the order."));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    const requestedOrder = searchParams.get("open");
    if (requestedOrder) openOrder(requestedOrder);
  }, [openOrder, searchParams, selectedStore?.public_id]);

  useEffect(() => {
    const notificationPublicId = searchParams.get("notification");
    if (!notificationPublicId || markedNotifications.current.has(notificationPublicId)) return;
    markedNotifications.current.add(notificationPublicId);
    markNotificationRead(notificationPublicId).catch(() => {
      markedNotifications.current.delete(notificationPublicId);
    });
  }, [searchParams]);

  const closeOrder = () => {
    if (saving) return;
    setSelected(null);
    setAction("");
    setReason("");
    if (searchParams.has("open")) {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      next.delete("notification");
      setSearchParams(next, { replace: true });
    }
  };

  const runAction = async () => {
    if (!selected || !action) return;
    if (["cancel", "reject"].includes(action) && reason.trim().length < 3) {
      setError("Enter a reason of at least 3 characters.");
      return;
    }
    const payload = {
      expected_version: selected.version,
      reason: reason.trim() || null,
      ...(action === "complete" ? { payment_status: paymentStatus } : {}),
    };
    const handlers = {
      confirm: confirmOrder,
      preparing: markOrderPreparing,
      ready: markOrderReady,
      complete: completeOrder,
      cancel: cancelOrder,
      reject: rejectOrder,
    };
    setSaving(true);
    setError("");
    try {
      const response = await handlers[action](selected.public_id, payload);
      setSelected(response.data);
      setAction("");
      setReason("");
      setSuccess(`Order ${response.data.order_number} updated.`);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not update the order."));
    } finally {
      setSaving(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const actions = useMemo(() => nextActions(selected), [selected]);

  return (
    <MainLayout>
      <PageHeader
        title="Orders"
        description={`WhatsApp orders, stock reservations, preparation, and completion for ${selectedStore?.name}.`}
        actions={(
          <>
            <Chip label={`${total.toLocaleString()} orders`} sx={{ fontWeight: 800 }} />
            <Button variant="outlined" onClick={load} disabled={loading}>
              Refresh
            </Button>
          </>
        )}
      />

      {!canEdit && <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>You have read-only access to orders.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        <TextField fullWidth label="Search orders" placeholder="Order number, customer, or phone" value={query} onChange={(event) => setQuery(event.target.value)} sx={{ maxWidth: 620 }} />
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {STATUSES.map((value) => <MenuItem key={value || "all"} value={value}>{value ? titleCase(value) : "All statuses"}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {loading ? <LoadingPanel label="Loading orders…" /> : items.length === 0 ? (
        <EmptyPanel icon="🛒" title="No orders found" description="Confirmed customer purchases from WhatsApp will appear here." />
      ) : (
        <>
          <Grid container spacing={2.5}>
            {items.map((order) => (
              <Grid item xs={12} md={6} lg={4} key={order.public_id}>
                <Card sx={{ height: "100%", borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 14px 35px rgba(15,23,42,.05)" }}>
                  <CardContent sx={{ p: 2.75 }}>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Box>
                        <Typography fontWeight={900}>{order.order_number}</Typography>
                        <Typography color="text.secondary" variant="body2">{order.customer_name}</Typography>
                      </Box>
                      <Chip label={titleCase(order.status)} color={statusColor(order.status)} size="small" sx={{ fontWeight: 800 }} />
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={0.8}>
                      <Typography variant="body2"><strong>Total:</strong> {formatMoney(order.total, order.currency)}</Typography>
                      <Typography variant="body2"><strong>Fulfillment:</strong> {titleCase(order.fulfillment_method)}</Typography>
                      <Typography variant="body2"><strong>Payment:</strong> {titleCase(order.payment_method)} · {titleCase(order.payment_status)}</Typography>
                      <Typography variant="body2"><strong>Source:</strong> {titleCase(order.source)}</Typography>
                      {order.reservation_expires_at && <Typography variant="body2"><strong>Reserved until:</strong> {formatDate(order.reservation_expires_at)}</Typography>}
                    </Stack>
                    <Button fullWidth variant="outlined" sx={{ mt: 2.2, textTransform: "none", fontWeight: 800 }} onClick={() => openOrder(order.public_id)}>Open order</Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {pageCount > 1 && <Stack alignItems="center" sx={{ mt: 4 }}><Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" shape="rounded" /></Stack>}
        </>
      )}

      <Dialog open={Boolean(selected) || detailLoading} onClose={closeOrder} fullWidth maxWidth="md">
        <DialogTitle fontWeight={900}>{selected?.order_number || "Loading order…"}</DialogTitle>
        <DialogContent>
          {detailLoading || !selected ? <LoadingPanel label="Loading order…" /> : (
            <Stack spacing={2.2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={titleCase(selected.status)} color={statusColor(selected.status)} sx={{ fontWeight: 800 }} />
                <Chip label={`Reservation: ${titleCase(selected.reservation_status)}`} variant="outlined" />
                <Chip label={`Payment: ${titleCase(selected.payment_status)}`} variant="outlined" />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Customer</Typography><Typography fontWeight={800}>{selected.customer_name}</Typography><Typography variant="body2">{selected.customer_phone || "No phone"}</Typography></Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Fulfillment</Typography>
                  <Typography fontWeight={800}>{titleCase(selected.fulfillment_method)}</Typography>
                  <Typography variant="body2">
                    {selected.fulfillment_method === "delivery"
                      ? [selected.delivery_area, selected.delivery_address].filter(Boolean).join(" · ") || "Delivery address unavailable"
                      : "Store pickup"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider />
              <Typography variant="h6" fontWeight={900}>Items</Typography>
              {selected.items.map((item) => (
                <Box key={item.public_id} sx={{ p: 2, borderRadius: 3, bgcolor: "#f8fafc" }}>
                  <Stack direction="row" justifyContent="space-between" gap={2}>
                    <Box><VariantSnapshotLabel productName={item.product_name} variantLabel={item.variant_label} /><Typography variant="body2" color="text.secondary">Qty {item.quantity} · Reserved {item.reserved_quantity}</Typography></Box>
                    <Typography fontWeight={900}>{formatMoney(item.line_total, selected.currency)}</Typography>
                  </Stack>
                </Box>
              ))}

              <Divider />
              <Stack alignItems="flex-end" spacing={0.5}>
                <Typography>List subtotal: {formatMoney(selected.subtotal, selected.currency)}</Typography>
                {Number(selected.discount_total) > 0 && <Typography>Discount: -{formatMoney(selected.discount_total, selected.currency)}</Typography>}
                {Number(selected.tax_total) > 0 && <Typography>Tax: {formatMoney(selected.tax_total, selected.currency)}</Typography>}
                {Number(selected.delivery_fee) > 0 && <Typography>Delivery: {formatMoney(selected.delivery_fee, selected.currency)}</Typography>}
                <Typography variant="h6" fontWeight={900}>Total: {formatMoney(selected.total, selected.currency)}</Typography>
              </Stack>

              {selected.events.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="h6" fontWeight={900}>History</Typography>
                  <Stack spacing={1}>
                    {selected.events.map((event) => <Box key={event.public_id}><Typography fontWeight={800}>{titleCase(event.event_type)}</Typography><Typography variant="caption" color="text.secondary">{formatDate(event.created_at)}{event.reason ? ` · ${event.reason}` : ""}</Typography></Box>)}
                  </Stack>
                </>
              )}

              {canEdit && actions.length > 0 && (
                <Card sx={{ borderRadius: 3, boxShadow: "none", border: "1px solid #cbd5e1" }}>
                  <CardContent>
                    <Typography fontWeight={900} sx={{ mb: 1.5 }}>Update order</Typography>
                    <FormControl fullWidth sx={{ mb: 1.5 }}>
                      <InputLabel>Action</InputLabel>
                      <Select label="Action" value={action} onChange={(event) => setAction(event.target.value)}>
                        {actions.map((value) => <MenuItem key={value} value={value}>{titleCase(value)}</MenuItem>)}
                      </Select>
                    </FormControl>
                    {action === "complete" && (
                      <FormControl fullWidth sx={{ mb: 1.5 }}>
                        <InputLabel>Payment status</InputLabel>
                        <Select label="Payment status" value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value)}>
                          {['unpaid', 'pending', 'paid'].map((value) => <MenuItem key={value} value={value}>{titleCase(value)}</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}
                    <TextField fullWidth multiline minRows={2} label={['cancel', 'reject'].includes(action) ? "Reason (required)" : "Internal note (optional)"} value={reason} onChange={(event) => setReason(event.target.value)} />
                  </CardContent>
                </Card>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={closeOrder} disabled={saving}>Close</Button>
          {canEdit && action && <Button variant="contained" color={['cancel', 'reject'].includes(action) ? 'error' : 'primary'} onClick={runAction} disabled={saving}>{saving ? "Saving…" : titleCase(action)}</Button>}
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
